import { createPortal } from 'react-dom'
import { useEffect } from 'react'
import { X } from 'lucide-react'
import { Button } from './Button'

interface ConfirmDialogProps {
  title: string
  children: React.ReactNode
  confirmLabel: string
  cancelLabel?: string
  isLoading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/** Boite de dialogue de confirmation (remplace window.confirm). */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel = 'Annuler',
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) onCancel()
    }
    window.addEventListener('keydown', onEsc)
    return () => window.removeEventListener('keydown', onEsc)
  }, [onCancel, isLoading])

  const dialog = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        className="relative w-full max-w-md rounded-2xl border border-[#E2E4ED] bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-[#E2E4ED] px-6 py-4">
          <h2 id="confirm-dialog-title" className="text-base font-semibold text-[#1B2559]">
            {title}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            aria-label="Fermer la boite de dialogue"
            className="rounded-lg p-1 text-[#8B8FA7] hover:bg-[#F4F5FA] hover:text-[#1B2559] disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-4 text-sm text-[#555A73] space-y-2">{children}</div>

        <div className="flex justify-end gap-3 border-t border-[#E2E4ED] px-6 py-4">
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button onClick={onConfirm} isLoading={isLoading}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )

  return createPortal(dialog, document.body)
}
